import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MedicineService } from 'src/core/services/medicine.service';
import { DosageType, DosageUnit } from 'src/interfaces/medicine';

@Component({
  selector: 'core-dosage-type-manage-list',
  templateUrl: './dosage-type-manage-list.component.html',
  styleUrls: ['./dosage-type-manage-list.component.scss'],
  standalone: false
})
export class DosageTypeManageListComponent implements OnInit, OnDestroy {

  @Input({ required: true }) dosageUnits!: DosageUnit[];
  @Output() dosageTypesChange = new EventEmitter<DosageType[]>();

  dosageTypes: DosageType[] = [];
  searchControl = new FormControl('');
  isSearching = false;

  private readonly destroy$ = new Subject<void>();
  private readonly searchLimit = 50;

  constructor(private _medicineService: MedicineService) { }

  ngOnInit(): void {
    this.loadDosageTypes('');

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((search: string | null) => {
        this.loadDosageTypes(search ?? '');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadDosageTypes(search: string): Promise<void> {
    this.isSearching = true;

    try {
      this.dosageTypes = await this._medicineService.getDosageTypes(1, this.searchLimit, search);
      this.dosageTypesChange.emit(this.dosageTypes);
    } finally {
      this.isSearching = false;
    }
  }

  async createDosageType(): Promise<void> {
    const newDosageType: DosageType = await this._medicineService
      .openDosageTypeCreateDialog(
        'New dosage type',
        'Create new dosage type',
        this.dosageUnits
      );

    if (!newDosageType) {
      return;
    }

    await this.loadDosageTypes(this.searchControl.value ?? '');
  }

  async deleteDosageType(type: DosageType): Promise<void> {
    const success: boolean = await this._medicineService.deleteDosageType(type.id);

    if (success) {
      this.dosageTypes = this.dosageTypes.filter(dosageType => dosageType.id !== type.id);
      this.dosageTypesChange.emit(this.dosageTypes);
    }
  }

}
